import { Alert, Link } from '@ghs/components';
import { Box, CircularProgress, Container } from '@mui/material';
import { useUnit } from 'effector-react';
import React, { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import BiDashboardEmbed from '../../components/BiDashboardEmbed/BiDashboardEmbed';
import { $$bi, UserDashboardStatus } from '../../services/BiService';
import CuratingMessage from './CuratingMessage';
import { hasDashboardPermission } from '../../services/PermissionService/PermissionService.ts';
import { $$app } from '../../components/App/model.tsx';
import { $$contactService } from '../../services/ContactService/model';

type BiDashboardSigmaProps = {
  overridePageTitle?: boolean;
};

export default function BiDashboardSigma({ overridePageTitle = true }: BiDashboardSigmaProps) {
  const userDetails = useUnit($$app.$userDetails);
  const queryParameters = new URLSearchParams(window.location.search);
  const sigmaInfo = useUnit($$bi.$sigmaInfo);
  const sigmaDashboardStatus = useUnit($$bi.$sigmaDashboardStatus);
  const reloadSigmaInfoRetryCount = useUnit($$bi.$reloadSigmaInfoRetryCount);
  const contactInfo = useUnit($$contactService.$contactInfo);
  const dashboardId = queryParameters.get('dashboardId') || sigmaInfo?.defaultDashboardId;
  const bookmarkId = queryParameters.get('bookmarkId');

  React.useEffect(() => {
    /**
     * Initializes the component
     *
     * @returns {Promise<void>} Promise with the result of initialization
     */
    async function init() {
      // Update the browser tab title with the name of the dashboard. We could avoid this by adding the
      // dashboard title as a query parameter, but would make the URL to look ugly. The dashboard info is
      // loaded into session so this doesn't require additional calls to Sigma server.
      if (overridePageTitle) {
        const dashboard = sigmaInfo?.dashboards?.find(item => item.id === dashboardId);
        if (dashboard?.title) {
          document.title = dashboard.title;
        }
      }
    }
    init();
  }, [sigmaInfo]);

  const userHasAccessToDashboard = () => {
    // When the user is authenticated but doesn't have access to the dashboard
    return sigmaInfo?.dashboards.some(dashboard => dashboard.id === dashboardId);
  };

  if (!hasDashboardPermission(userDetails?.permissions)) {
    return <Container data-testid="bi-dashboard-empty-container" />;
  }

  switch (sigmaDashboardStatus) {
    case UserDashboardStatus.FIRST_AUTHENTICATION_ATTEMPT:
      return (
        <Box id="authenticating-msg" display="flex" justifyContent="center" alignItems="center" mt={1}>
          <CircularProgress />
        </Box>
      );
    case UserDashboardStatus.RETRYING_AUTHENTICATION:
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box sx={{ maxWidth: 'md' }}>
            <Alert id="retrying-authentication-msg" severity="warning" sx={{ mb: 2, ml: 3, mr: 3 }}>
              <Fragment>
                Aw Snap! We hit a glitch. Our system is automatically trying to load your dashboards, and we appreciate your patience as it may take us up to 60 seconds to get over the hump.
                <br />
                <br />
                <CircularProgress size="1.5rem" /> Attempt {reloadSigmaInfoRetryCount}. Trying to reconnect...
              </Fragment>
            </Alert>
          </Box>
        </Box>
      );
    case UserDashboardStatus.MAX_AUTHENTICATION_RETRIES_REACHED:
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box sx={{ maxWidth: 'md' }}>
            <Alert id="failed-authenticated-msg" severity="error" sx={{ mb: 2, ml: 3, mr: 3 }}>
              <Fragment>
                Unfortunately our glitch turned into a brick wall and we are unable to resolve the issue. Please reach out to{' '}
                {contactInfo?.email ? <Link href={`mailto:${contactInfo.email}`}>{contactInfo.email}</Link> : 'your tracking supplier'} and provide your email address that was used, along with the
                date/time of the instance. We apologize for the inconvenience.
              </Fragment>
            </Alert>
          </Box>
        </Box>
      );
    case UserDashboardStatus.USER_NOT_FULLY_SETUP:
      return <CuratingMessage />;
  }

  if (sigmaDashboardStatus === UserDashboardStatus.USER_AUTHENTICATED_WITH_DASHBOARDS && !userHasAccessToDashboard()) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box sx={{ maxWidth: 'md' }}>
          <Alert id="no-access-to-dashboard-msg" severity="warning" sx={{ mb: 2, ml: 3, mr: 3 }}>
            <Fragment>
              Hmmm... that dashboard does not exist but there are others within your{' '}
              <Link component={RouterLink} to={'/dashboards'}>
                dashboards page
              </Link>{' '}
              that are ready and waiting!
            </Fragment>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <BiDashboardEmbed workbookId={dashboardId} bookmarkId={bookmarkId} />
    </Box>
  );
}
